import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import React from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState, useResetRecoilState } from "recoil";
import { domainListAtom, modalAtom } from "../../containers/App/App.state";
import FlokkButton from "../FlokkButton";
import DomainForm from "./DomainForm";
import { makeStyles } from "@material-ui/core/styles";
import { Pagination } from "@material-ui/lab";
import { chunk, cloneDeep } from "lodash";
import CancelIcon from "@material-ui/icons/Cancel";
import AddIcon from "@material-ui/icons/Add";
import { saveDomain } from "../../helpers/services/ContractServices";
import { useQueryClient } from "react-query";

const translationPath = "flokk_onlinedomain";

const useDomainTableStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gap: theme.spacing(2),
  },
  add_btn: {
    placeSelf: "start",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const DomainTable = ({ trader_company_id, status }) => {
  const classes = useDomainTableStyles();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [{ domains }, setDomains] = useRecoilState(domainListAtom);
  const resetDomains = useResetRecoilState(domainListAtom);
  const setModal = useSetRecoilState(modalAtom);
  const canModify = ["negotiating", "rejected"].includes(status);

  function handleRowClick(row) {
    if (canModify) {
      setModal({
        content: <DomainForm row={row} />,
      });
    }
  }

  function handleAdd() {
    setModal({
      content: <DomainForm />,
    });
  }

  function handleDelete(e, row) {
    e.stopPropagation();

    if (typeof row.id === "number") {
      const domainIndex = domains.findIndex((domain) => domain.id === row.id);
      const clonedDomains = cloneDeep(domains);
      const newData = {
        ...clonedDomains[domainIndex],
        _state: "delete",
      };
      clonedDomains[domainIndex] = newData;
      setDomains({
        trader_company_id,
        domains: clonedDomains,
      });
    } else {
      setDomains({
        trader_company_id,
        domains: domains.filter((domain) => domain.id !== row.id),
      });
    }
  }

  async function handleSave() {
    await saveDomain(trader_company_id, domains);
    resetDomains();
    await qc.invalidateQueries(["Domain", trader_company_id]);
  }

  function handlePageChange(e, value) {
    setPage(value);
  }

  const filteredDomains = domains.filter(
    (domain) => domain?.["_state"] !== "delete"
  );

  const pageItems = chunk(cloneDeep(filteredDomains), 10)[page - 1];

  return (
    <div className={classes.root}>
      {canModify && (
        <div className={classes.add_btn}>
          <FlokkButton
            startIcon={<AddIcon />}
            color="primary"
            variant="contained"
            onClick={handleAdd}
          >
            {t("system.add_btn")}
          </FlokkButton>
        </div>
      )}
      <TableContainer component={Paper} variant="outlined" square>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canModify && <TableCell>{t("table.actions")}</TableCell>}
              <TableCell>{t(translationPath + ".domain")}</TableCell>
              <TableCell>{t(translationPath + ".country_id")}</TableCell>
              <TableCell>
                {t(translationPath + ".verified_logistics_flag")}
              </TableCell>
              <TableCell>
                {t(translationPath + ".verified_service_flag")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(pageItems || []).map((row) => (
              <TableRow
                key={row.id}
                onClick={() => handleRowClick(row)}
                hover={canModify}
              >
                {canModify && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, row)}
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                )}
                <TableCell>{row.domain}</TableCell>
                <TableCell>{row.country_id_short_name}</TableCell>
                <TableCell>
                  {row.verified_logistics_flag ? "Ja" : "Nein"}
                </TableCell>
                <TableCell>
                  {row.verified_service_flag ? "Ja" : "Nein"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={classes.footer}>
        <Pagination
          color="primary"
          count={Math.ceil(filteredDomains.length / 10)}
          page={page}
          onChange={handlePageChange}
        />
        {canModify && (
          <FlokkButton color="primary" variant="contained" onClick={handleSave}>
            {t("system.save_btn")}
          </FlokkButton>
        )}
      </div>
    </div>
  );
};

export default DomainTable;
